import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Box,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloseIcon from "@mui/icons-material/Close";
import api from "../axiosConfig";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

export default function BlacklistReviewPanel({ open, onClose, onChanged }) {
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [actionBusy, setActionBusy] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/blacklist", { params: { page: p, per_page: 10 } });
      const data = res.data;
      setContacts(data.data || []);
      setPage(data.current_page || 1);
      setLastPage(data.last_page || 1);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load blacklisted contacts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load(1);
      setActionError("");
      setActionSuccess("");
    }
  }, [open, load]);

  const handleRestore = async (contact) => {
    setActionBusy(contact.id);
    setActionError("");
    setActionSuccess("");
    try {
      await api.post(`/blacklist/${contact.id}/restore`);
      setActionSuccess(`${contact.name || "Contact"} restored to active successfully.`);
      await load(page);
      onChanged?.();
    } catch (e) {
      setActionError(e?.response?.data?.message || "Failed to restore contact.");
    } finally {
      setActionBusy(null);
    }
  };

  const handleDelete = async (contact) => {
    if (
      !window.confirm(
        `Permanently delete "${contact.name || "this contact"}"?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }
    setActionBusy(contact.id);
    setActionError("");
    setActionSuccess("");
    try {
      await api.delete(`/blacklist/${contact.id}`);
      setActionSuccess(`${contact.name || "Contact"} permanently deleted.`);
      await load(page);
      onChanged?.();
    } catch (e) {
      setActionError(e?.response?.data?.message || "Failed to delete contact.");
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.2} alignItems="center">
            <BlockIcon color="error" />
            <Typography variant="h6" fontWeight={900}>
              Blacklisted Contacts
              {total > 0 ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ ml: 1 }}
                >
                  ({total})
                </Typography>
              ) : null}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {actionSuccess ? (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setActionSuccess("")}
          >
            {actionSuccess}
          </Alert>
        ) : null}

        {actionError ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setActionError("")}
          >
            {actionError}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <BlockIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography color="text.secondary">No blacklisted contacts.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                  <TableCell sx={{ fontWeight: 900 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Email / Phone</TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="center">
                    Strikes
                  </TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Last Failure</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Blacklisted At</TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800}>
                        {c.name || "—"}
                      </Typography>
                      {c.company ? (
                        <Typography variant="caption" color="text.secondary">
                          {c.company}
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" display="block">
                        {c.email || "—"}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.phone || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={c.failed_strikes ?? 0}
                        color={(c.failed_strikes ?? 0) >= 3 ? "error" : "warning"}
                        sx={{ fontWeight: 900 }}
                      />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography
                        variant="caption"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {c.last_failure_reason || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">
                        {fmtDate(c.blacklisted_at)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Restore to active">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={actionBusy === c.id}
                              onClick={() => handleRestore(c)}
                            >
                              {actionBusy === c.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RestoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Permanently delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={actionBusy === c.id}
                              onClick={() => handleDelete(c)}
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {lastPage > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={lastPage}
              page={page}
              onChange={(_, p) => load(p)}
              color="primary"
            />
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
